import FullPageDialog from 'app/components/full-page-dialog'
import TextInput from 'app/components/text-input'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { AssetsService } from 'app/modules/shared/services'
import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { SignalVerdict, ThreatIndicatorsCategory } from 'app/modules/reports/types'
import { logger } from 'app/components'
import { FilterValues } from '../filter-values'
import { castStrengthToVerdict } from 'app/modules/reports/helpers'
import { IndicatorFilters, IndicatorListHeader } from './indicator-filters'
import { CategoryList } from './category-list'

interface ThreatIndicatorsSearchProps {
  onApply: (indicators: string[], ...args: any[]) => void
  searchIndicators: string[]
  filterId?: string
  value?: string
}

const ThreatIndicatorsSearch: React.FC<ThreatIndicatorsSearchProps> = ({
  onApply,
  searchIndicators,
  filterId,
  value,
}) => {
  const [isOpen, setOpen] = useState(false)
  const [allIndicators, setAllIndicators] = useState<ThreatIndicatorsCategory[]>([])
  const [filteredIndicators, setFilteredIndicators] = useState<ThreatIndicatorsCategory[]>([])
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>(searchIndicators)
  const [allSelected, setAllSelected] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [mimeFilter, setMimeFilter] = useState('')
  const [mimeTypes, setMimeTypes] = useState<string[]>([])
  const [verdictFilter, setVerdictFilter] = useState<SignalVerdict[]>(Object.values(SignalVerdict))
  const [openTypes, setOpenTypes] = useState<string[]>([])
  const [openAll, setOpenAll] = useState(false)
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [triggerSubmit, setTriggerSubmit] = useState(false)
  const [enableSubmit, setEnableSubmit] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [categories, setCategories] = useState<string[]>([])
  const [openCategories, setOpenCategories] = useState<string[]>([])
  const filterValues = value ? value.split(',') : []

  const { _t } = useTypedTranslation()

  const getIndicatorsNumber = (indicators: ThreatIndicatorsCategory[]) =>
    indicators.reduce((sum: number, type: ThreatIndicatorsCategory) => (sum += type.groups.length), 0)

  const getSelectedTypesFromSearchParams = useCallback(() => {
    return filteredIndicators
      .filter((indicator) => indicator.groups.every((group) => searchIndicators.includes(group.id)))
      .map((indicator) => indicator.type)
  }, [searchIndicators, filteredIndicators])

  const handleVerdictFilterChange = (verdict: SignalVerdict) => {
    setVerdictFilter((prevVerdict) =>
      prevVerdict.includes(verdict) ? prevVerdict.filter((_verdict) => _verdict !== verdict) : [...prevVerdict, verdict]
    )
  }

  const handleSelectAll = () => {
    const isAllSelectedNow = !allSelected
    setSelectedTypes(isAllSelectedNow ? filteredIndicators.map((_type) => _type.type) : [])
    setSelectedIndicators(
      isAllSelectedNow
        ? Array.from(new Set(filteredIndicators.flatMap((_type) => _type.groups.map((group) => group.id))))
        : []
    )
    setAllSelected(isAllSelectedNow)
  }

  const handleOpenAccordion = (type: string) =>
    setOpenTypes(openTypes.includes(type) ? openTypes.filter((_type) => _type !== type) : [...openTypes, type])

  const handleOpenCategory = (category: string) =>
    setOpenCategories(
      openCategories.includes(category)
        ? openCategories.filter((_cat) => _cat !== category)
        : [...openCategories, category]
    )

  const handleOpenAll = () => {
    setOpenAll(!openAll)
  }

  const handleSelectType = (type: string) => {
    const isTypeSelectedNow = !selectedTypes.includes(type)
    const selectedGroupIds =
      filteredIndicators.find((_type) => _type.type === type)?.groups.map((group) => group.id) ?? []

    if (isTypeSelectedNow) {
      setSelectedIndicators([...new Set([...selectedIndicators, ...selectedGroupIds])])
    } else {
      setSelectedIndicators(selectedIndicators.filter((id) => !selectedGroupIds.includes(id)))
    }

    setSelectedTypes(isTypeSelectedNow ? [...selectedTypes, type] : selectedTypes.filter((_type) => _type !== type))

    !openTypes.includes(type) && handleOpenAccordion(type)

    setEnableSubmit(!!searchIndicators.length)
  }

  const handleSelectIndicator = (indicator: string) => {
    const newSelectedList = selectedIndicators.includes(indicator)
      ? selectedIndicators.filter((_rule) => _rule !== indicator)
      : [...selectedIndicators, indicator]

    setSelectedIndicators(newSelectedList)
    setAllSelected(newSelectedList.length === filteredIndicators.length)

    const indicatorType =
      filteredIndicators.find((_type) => _type.groups.find((_group) => _group.id === indicator))?.type ?? ''
    const typeGroups = filteredIndicators.find((_type) => _type.type === indicatorType)

    if (typeGroups) {
      const groupIds = typeGroups.groups.map((group) => group.id)
      const isSelected = groupIds.every((id) => newSelectedList.includes(id))
      setSelectedTypes(
        isSelected ? [...selectedTypes, indicatorType] : selectedTypes.filter((_type) => _type !== indicatorType)
      )
    }

    // Enable to save cleared filter
    setEnableSubmit(!!searchIndicators.length)
  }

  const handleClearSelection = () => {
    setSelectedIndicators([])
    setSelectedTypes([])
    setAllSelected(false)
    // Enable to save cleared filter
    setEnableSubmit(!!searchIndicators.length)
  }

  const handleRemoveIndicator = (rule: string) => handleSelectIndicator(rule)

  const handleRemoveFromInput = (rule: string, e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    handleRemoveIndicator(rule)
    setTriggerSubmit(true)
  }

  const handleSubmit = () => {
    onApply(selectedIndicators)
  }

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
  }

  const filteredBySearch = useMemo(() => {
    if (!searchInput) {
      return allIndicators
    }

    return allIndicators
      .map((_type) => ({
        ..._type,
        groups: _type.groups.filter((group) => {
          const groupId = group.id.toString().toLowerCase()
          const groupDescription = group.description ? group.description.toLowerCase() : ''
          const searchInputLower = searchInput.toLowerCase()

          return groupId.includes(searchInputLower) || groupDescription.includes(searchInputLower)
        }),
      }))
      .filter((_type) => _type.groups.length > 0)
  }, [searchInput, allIndicators])

  useEffect(() => {
    const filteredList = filteredBySearch
      .map((_type) => ({
        type: _type.type,
        name: _type.name,
        category: _type.category,
        groups: _type.groups.filter(
          (_group) =>
            (mimeFilter ? _group.mime_type?.includes(mimeFilter) : true) &&
            (_group.strength === null ||
              typeof _group.strength === 'undefined' ||
              verdictFilter.includes(castStrengthToVerdict(_group.strength)))
        ),
      }))
      .filter((_type) => _type.groups.length > 0)

    setFilteredIndicators(filteredList)
    setAllSelected(false)
  }, [mimeFilter, verdictFilter, filteredBySearch])

  // open all accordions when searching
  useEffect(() => {
    const isFiltered = searchInput.length > 0
    setOpenAll(isFiltered)
  }, [searchInput])

  useEffect(() => {
    setOpenTypes(openAll ? filteredIndicators.map((type) => type.type) : [])
    setOpenCategories(openAll ? categories : [])
  }, [openAll, filteredIndicators, categories])

  useEffect(() => {
    if (triggerSubmit) {
      setTriggerSubmit(false)
      handleSubmit()
    }
  }, [triggerSubmit])

  useEffect(() => {
    if (!isOpen) {
      setIsFirstLoad(true)
      setOpenTypes([])
      setOpenAll(false)
      setOpenCategories([])
      return
    }

    const fetchIndicators = async () => {
      try {
        const mimes = await AssetsService.getMimeTypes()
        const indicators = await AssetsService.getThreatIndicators()

        const allCategories = indicators.map((indicator) => indicator.category)
        const uniqueCategories = Array.from(new Set(allCategories)).filter(Boolean) as string[]

        setMimeTypes(mimes)
        setAllIndicators(indicators)
        setFilteredIndicators(indicators)
        setCategories(uniqueCategories)
      } catch (e) {
        logger.error(e)
        setHasError(true)
      }
    }

    fetchIndicators()
  }, [isOpen])

  useEffect(() => {
    if (!isFirstLoad || !(isOpen && filteredIndicators.length)) return

    setIsFirstLoad(false)
    setSelectedIndicators(searchIndicators)
    setSelectedTypes(getSelectedTypesFromSearchParams())
  }, [searchIndicators, isOpen, filteredIndicators]) // Triggered if indicators removed from threat hunting's bottom filter summary

  const IndicatorSearchContent = () =>
    isOpen ? (
      <div className="indicator-search-container">
        <IndicatorFilters
          verdictFilter={verdictFilter}
          onVerdictFilterChange={handleVerdictFilterChange}
          mimeFilter={mimeFilter}
          mimeTypes={mimeTypes}
          setMimeFilter={setMimeFilter}
          searchInput={searchInput}
          onSearchChange={handleSearchChange}
        />
        <div className="indicators-search-body">
          <IndicatorListHeader
            indicatorsCount={getIndicatorsNumber(filteredIndicators)}
            allSelected={allSelected}
            onSelectAll={handleSelectAll}
            onOpenAll={handleOpenAll}
            value={openAll}
          />
          <CategoryList
            categories={categories}
            filteredIndicators={filteredIndicators}
            openCategories={openCategories}
            openTypes={openTypes}
            selectedTypes={selectedTypes}
            selectedIndicators={selectedIndicators}
            onOpenCategory={handleOpenCategory}
            onOpenAccordion={handleOpenAccordion}
            onSelectType={handleSelectType}
            onSelectIndicator={handleSelectIndicator}
          />
        </div>
      </div>
    ) : (
      <></>
    )

  return (
    <>
      <p className="text-note text-m m-0 pb-1">{_t('threat-indicators')}</p>
      <div onClick={() => setOpen(true)}>
        <TextInput
          inputClassName="tags-filter-input"
          id={filterId}
          placeholder={`${_t('browse')} ${_t('threat-indicators')}`}
          value={
            filterValues.length ? (
              <FilterValues values={filterValues} showAll={false} onRemove={handleRemoveFromInput} />
            ) : (
              ''
            )
          }
          postfix={<i className="ds3-icon ds3-external-link ds3-hover" />}
          cursor="pointer"
          noWrap
          readOnly
        />
      </div>
      {isOpen ? (
        <FullPageDialog
          body={
            hasError ? (
              <span>{_t('There was an error during fetching threat indicators')}</span>
            ) : (
              IndicatorSearchContent()
            )
          }
          title={_t('threat-indicators')}
          description=""
          isOpen
          onClose={() => setOpen(false)}
          filteredList={selectedIndicators}
          contentLength={getIndicatorsNumber(allIndicators)}
          onClearSelection={handleClearSelection}
          onRemoveFilter={handleRemoveIndicator}
          onApply={handleSubmit}
          enableSubmit={enableSubmit}
          filterSummaryLabel={`${_t('threat-indicators-selected')}:`}
          noFilterLabel={_t('no-threat-indicator-selected')}
          applyButtonLabel={_t('apply')}
        />
      ) : null}
    </>
  )
}

export default ThreatIndicatorsSearch
